import { Bool, SegmentType } from '@ve-pro/enums/segment'
import { TrackType } from '@ve-pro/enums/track-type'
import { useTrackStore, useProjectStore } from '@ve-pro/store'
import type {
  SegmentCollection,
  Track,
  SegmentGroupCollection,
  SegmentNoGroupCollection,
  SegmentReplaceGroupCollection,
  Segment,
  SegmentVideo,
  SegmentSubtitleGroup,
  SegmentReplaceableCollection,
} from '@ve-pro/types/track.type'
import { generateTrackId } from './generate-id'
import {
  getGroupBySegment,
  isSegmentGroup,
  isSegmentGroupChild,
} from './segment/segment-group'
import {
  SupportTransitionSegment,
  getTransition,
  removeTransitionNotUpdate,
} from './segment/segment-transition'
import { updateMainTrack } from './update-main-track'
import { add } from '@com/utils/math'
import { ProjectMode } from '@ve-pro/enums/project'
import { setActiveSegment, toggleActiveSegment } from './toggle-active-segment'
import { useSegmentGroupHelper } from '@ve-pro/ve/hotkeys/shortcuts/group/handle'
import _ from 'lodash'
import { getFullFrameMs, isFullFrame } from './frame'
import { isSegmentFragment } from './segment/segment-fragment'
import { isSegmentSubtitleGroup } from './segment/segment-subtitle'
import { omit } from 'lodash-es'
import { isSegmentReplaceable } from './segment/segment-replaceable'

export { addSegmentbyCurrentTime } from './add-segment-action'

export function addSegment(target: SegmentCollection, track: Track): void {
  // 添加轨道的信息
  target.track_id = track.id
  track.segments.push(target as any)
}

// 兼容转场的删除逻辑(只删除视频元件的后转场，常用于视频的拖拽到其他轨道)
export function removeTransitionIfVideo(
  target: SegmentCollection,
  nextVideo?: SupportTransitionSegment,
): void {
  if (target.type !== SegmentType.Video) {
    return
  }

  const { before, behind } = getTransition(target)
  nextVideo = nextVideo || behind?.behind

  if (behind) {
    removeTransitionNotUpdate(behind)
  }

  if (before) {
    if (nextVideo) {
      before.behind = nextVideo
      nextVideo.before_transition_id = before.id
    } else {
      removeTransitionNotUpdate(before)
    }
  }

  target.before_transition_id = null
  target.behind_transition_id = null
}

export function removeSegmentSync(
  target: SegmentCollection,
  options?: {
    canRemoveTrackIfEmpty?: boolean
    excludeTarget?: boolean
  },
): void {
  const { canRemoveTrackIfEmpty = true, excludeTarget = false } = options ?? {}
  const trackStore = useTrackStore()

  const track = trackStore.tracks.find((t) => t.id === target.track_id)

  if (!track) {
    return
  }

  const relatedSegments = trackStore.segments.filter(
    (t) => t.source_segment_id > 0 && t.source_segment_id === target.id,
  )
  relatedSegments.forEach((relatedSegment) => {
    removeSegmentSync(relatedSegment, omit(options ?? {}, 'excludeTarget'))
  })

  if (!excludeTarget) {
    _removeSegment(target, track)
  }

  // 更新下选中的状态
  toggleActiveSegment(target, {
    actived: false,
    multiple: true,
  })
  if (trackStore.activeSegment?.id === target.id) {
    setActiveSegment()
  }

  if (trackStore.isMainTrackInBelongTracks(track)) {
    if (isSegmentFragment(target)) {
      trackStore.tracks
        .filter((t) => t.fragment_segment_id === target.id)
        .forEach((t) => {
          remove(t, trackStore.tracks)
        })
    }
    updateMainTrack()
  }

  if (!track.is_main && canRemoveTrackIfEmpty && track.segments.length === 0) {
    remove(track, trackStore.tracks)
  }

  // 检查是否全部轨道都空了
  if (trackStore.tracks.every((t) => t.segments.length === 0)) {
    trackStore.tracks.splice(0)
  }

  const tracks = trackStore.isEnterSegmentFragmentEditMode
    ? trackStore.currentEditingSegmentFragmentTracks ?? []
    : trackStore.defaultExcludeFragmentTracks

  if (trackStore.mainTack && tracks.every((t) => t.segments.length === 0)) {
    remove(trackStore.mainTack, trackStore.tracks)
  }
}

function _removeSegment(target: SegmentCollection, track?: Track): void {
  const trackStore = useTrackStore()
  track = track ?? trackStore.tracks.find((t) => t.id === target.track_id)
  if (track) {
    remove(target, track.segments)
    // 清楚轨道的信息
    target.track_id = ''

    // 拖拽出主轨道的元件，需要将is_main_video设置为false
    if (
      track.is_main &&
      target.type === SegmentType.Video &&
      target.is_main_video
    ) {
      target.is_main_video = false
    }
  }
}

export function removeSegment(
  target: SegmentCollection,
  canRemoveTrackIfEmpty = true,
): Promise<void> {
  const trackStore = useTrackStore()
  const track = trackStore.tracks.find((t) => t.id === target.track_id)
  if (track) {
    _removeSegment(target, track)

    // 地区资产裂变
    // 更新下选中的状态
    toggleActiveSegment(target, {
      actived: false,
      multiple: true,
    })
    if (trackStore.activeSegment?.id === target.id) {
      setActiveSegment()
    }

    if (trackStore.isMainTrackInBelongTracks(track)) {
      if (isSegmentFragment(target)) {
        trackStore.tracks
          .filter((t) => t.fragment_segment_id === target.id)
          .forEach((t) => {
            remove(t, trackStore.tracks)
          })
      }
      updateMainTrack()
    }

    if (canRemoveTrackIfEmpty) return removeTrackIfEmpty(track)
  }
  return Promise.resolve()
}

export function insertAfterTrack(
  track: Track | TrackType,
  target: Track,
  tracks?: Track[], // 作为被操作剧本
): Track {
  const trackStore = useTrackStore()
  // index + 1
  return insert(tracks || trackStore.tracks, track, target, 1)
}

export function insertBeforeTrack(
  track: Track | TrackType,
  target: Track,
  tracks?: Track[], // 作为被操作剧本
): Track {
  const trackStore = useTrackStore()
  // index 就是目标的index
  return insert(tracks || trackStore.tracks, track, target, 0)
}

export function removeTrackIfEmpty(track: Track, force = false): Promise<void> {
  const trackStore = useTrackStore()

  if (
    !trackStore.isMainTrackInBelongTracks(track) &&
    (force || track.segments.length === 0)
  ) {
    remove(track, trackStore.tracks)
  }

  /**
   * 异步检测并清除，原因：当轨道仅有一个元件时候，发了切换轨道的情况，切换过程中，删除操作会导致轨道中不存在元件的情况了
   */
  return new Promise((resolve) => {
    setTimeout(() => {
      // 检查是否全部轨道都空了
      if (trackStore.tracks.every((t) => t.segments.length === 0)) {
        trackStore.tracks.splice(0)
      }

      const tracks = trackStore.isEnterSegmentFragmentEditMode
        ? trackStore.currentEditingSegmentFragmentTracks ?? []
        : trackStore.defaultExcludeFragmentTracks

      if (trackStore.mainTack && tracks.every((t) => t.segments.length === 0)) {
        remove(trackStore.mainTack, trackStore.tracks)
      }

      resolve()
    })
  })
}

export function removeTrackIfEmptySync(track: Track, force = false): void {
  const trackStore = useTrackStore()

  if (
    !trackStore.isMainTrackInBelongTracks(track) &&
    (force || track.segments.length === 0)
  ) {
    remove(track, trackStore.tracks)
  }

  // 检查是否全部轨道都空了
  if (trackStore.tracks.every((t) => t.segments.length === 0)) {
    trackStore.tracks.splice(0)
  }

  const tracks = trackStore.isEnterSegmentFragmentEditMode
    ? trackStore.currentEditingSegmentFragmentTracks ?? []
    : trackStore.defaultExcludeFragmentTracks

  if (trackStore.mainTack && tracks.every((t) => t.segments.length === 0)) {
    remove(trackStore.mainTack, trackStore.tracks)
  }
}

export function remove<T extends { id: any }>(target: T, arr: T[]): void {
  const index = arr.findIndex((t) => t.id === target.id)
  if (index > -1) {
    arr.splice(index, 1)
  }
}

function isTrackType(type: any): type is TrackType {
  return typeof type === 'string'
}

function insert(
  tracks: Track[],
  track: Track | TrackType,
  target: Track,
  value: number,
): Track {
  const _track: Track = {
    id: generateTrackId(),
    segments: [],
    type: isTrackType(track) ? track : track.type,
    mute: false,
    is_hide: false,
    is_main: false,
  }

  const newTrack = isTrackType(track) ? _track : track
  const targetIndex = tracks.findIndex((t) => t.id === target.id)
  if (targetIndex > -1) {
    tracks.splice(targetIndex + value, 0, newTrack)
  }
  return newTrack
}

/**
 * 获取指定剧本的最大时长
 * @param tracks
 * @returns 时长
 */
export function getTracksMaxDuration(tracks: Track[]): number {
  const total = tracks.reduce((total, track) => {
    return track.segments.reduce((max, segment) => {
      let v = add(segment.start_time, segment.duration)
      if (isFullFrame(segment.start_time) && isFullFrame(segment.duration)) {
        v = getFullFrameMs(v)
      }
      return Math.max(max, v)
    }, total)
  }, 0)

  return Math.floor(total)
}

/**
 * 获取默认的剧本，不包含有字幕&配音的剧本
 * @param tracks Track[]
 * @returns Track[]
 */
export function getDefaultTracks(tracks: Track[]): Track[] {
  const isDefault = (v: any): v is ProjectMode.Default => {
    return !v || v === ProjectMode.Default
  }
  return tracks.filter(
    (t) =>
      t.segments.length === 0 || t.segments.some((seg) => isDefault(seg.stage)),
  )
}

export function isTemplateMainVideoSegment(
  segment: SegmentCollection,
): segment is SegmentVideo {
  return (
    isTemplateSegment(segment) &&
    segment.type === SegmentType.Video &&
    segment.is_main_video
  )
}

// 根据指定的元件，尝试获取对应的元件组，若不存在，则返回自身
export function tryGetTemplateSegmentGroup(
  segment: SegmentCollection,
): SegmentCollection {
  return isTemplateSegment(segment) && isSegmentGroupChild(segment)
    ? getGroupBySegment(segment) ?? segment
    : segment
}

export function isTemplateSegment(segment: SegmentCollection): boolean {
  return Boolean(segment.source?.startsWith('template-'))
}

export function isTemplateTrack(track: Track): boolean {
  return (
    Boolean(track.source?.startsWith('template-')) ||
    track.segments.some((seg) => isTemplateSegment(seg))
  )
}

/**
 * 处理tracks中‘可替换资源’资源转化成资源组
 * @param tracks Track[]
 */
export function tracksSegmentToGroup(tracks: Track[]) {
  const { toAddGroup } = useSegmentGroupHelper()
  const trackStore = useTrackStore()
  // const trackStore = useTrackStore()
  tracks.forEach((t) => {
    if (t.segments) {
      t.segments.forEach((s) => {
        // 判断普通元件 同时is_replaceable是可替换资源才可以
        // @ts-ignore
        if (!s.isGroup && s.is_replaceable === Bool.Truly) {
          toAddGroup(s).then((res) => {
            if (res && s.type === SegmentType.Text) {
              const child = trackStore.segmentGroups[res.id] ?? []
              child.forEach((segment) => {
                segment.auto_font_size = true
              })
            }
          })
        }

        if (
          s.isGroup &&
          s.is_replaceable === Bool.Truly &&
          s.type === SegmentType.Text
        ) {
          const child = trackStore.segmentGroups[s.id] ?? []
          child.forEach((segment) => {
            segment.auto_font_size = true
          })
        }
      })
    }
  })
}

export function getReplaceableSegments(
  segments: SegmentCollection[],
): SegmentReplaceableCollection[] {
  const replaceableGroups = segments
    .filter((s) => isSegmentReplaceable(s) && s.is_replaceable === Bool.Truly)
    .sort((a, b) => {
      if (a['type'] === SegmentType.Audio) {
        // @ts-ignore
        return a['type'] - b['type']
      }
      return a['start_time'] - b['start_time']
    }) as SegmentReplaceableCollection[]

  const mainVideo = replaceableGroups.filter(
    (s) => s.type === SegmentType.Video && s.is_main_video,
  )
  const others = replaceableGroups.filter(
    (s) => !(s.type === SegmentType.Video && s.is_main_video),
  )

  return [...mainVideo, ...others]
}

export function getAiRemoveSegmentGroups(): SegmentGroupCollection[] {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()

  return Object.values(projectStore.configs.aiRemove).reduce<
    SegmentGroupCollection[]
  >((_results, item) => {
    item.items.forEach((removeItem) => {
      const group = trackStore.segments
        .filter(isSegmentGroup)
        .find((s) => s.id === removeItem.segmentId)

      if (group) {
        _results.push(group)
      }
    })

    return _results
  }, [])
}

export function isFragmentTrack(track: Track): boolean {
  return (
    typeof track.fragment_segment_id === 'number' &&
    track.fragment_segment_id > 0
  )
}

export function groupFragmentTracks(
  tracks: Track[],
): Record<Segment['id'], Track[]> {
  return _.groupBy(
    tracks.filter((t) => t.fragment_segment_id && !isLangTrack(t)),
    'fragment_segment_id',
  )
}

/**
 * 是否是裂变元件所在的轨道
 * 根据lang是否有值进行判断
 * @param track 需要判断的轨道
 * @returns 是否
 */
export function isLangTrack(track: Track): boolean {
  return track.segments.some((t) => Boolean(t.lang))
}

export function isLangSegment(segment: SegmentCollection): boolean {
  return Boolean(segment.lang)
}

export function getLanguageTracks(tracks: Track[]): Track[] {
  return tracks
    .filter((t) => !t.segments.some((t) => t.lang !== ''))
    .filter((t) => t.segments.length || t.is_main)
}

export function getStoreAllSegments(): SegmentCollection[] {
  const trackStore = useTrackStore()
  const subtitleGroupSegments = trackStore.defaultTracks.reduce<
    SegmentSubtitleGroup[]
  >((acc, t) => [...acc, ...t.segments.filter(isSegmentSubtitleGroup)], [])

  return [...subtitleGroupSegments, ...trackStore.segments]
}
